/**
 * Normalize project slugs for API calls and PDP URLs.
 * Fixes links built with display titles (spaces) instead of API slugs (hyphens).
 */

import {
  enumerateApiSlugCandidatesFromSeoBefore,
  getPropertyTypeUrlWord,
  parseProjectRouteSlug,
} from './projectSeoSlug';

export function decodeRouteSlug(slug) {
  if (slug == null || slug === '') return '';
  try {
    return decodeURIComponent(String(slug));
  } catch {
    return String(slug);
  }
}

/** `Prestige Golden Grove` → `prestige-golden-grove` */
export function normalizeProjectSlugForApi(slug) {
  const raw = decodeRouteSlug(slug);
  if (!raw) return '';
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Prefer API slug fields; never use display title as URL segment. */
export function pickProjectSlugFromItem(item) {
  if (!item || typeof item !== 'object') return '';
  const raw = item.raw || item;
  const candidates = [
    item.slug,
    item.project_slug,
    item.url_slug,
    raw?.slug,
    raw?.project_slug,
    raw?.url_slug,
    item.apartment_buy_detail?.slug,
    item.villa_buy_detail?.slug,
    item.plot_buy_detail?.slug,
    item.details?.slug,
    raw?.details?.slug,
  ];
  for (const c of candidates) {
    const s = String(c ?? '').trim();
    if (!s) continue;
    if (/^\d+$/.test(s)) continue;
    return normalizeProjectSlugForApi(s) || s;
  }
  const id = String(item.id ?? item.project_id ?? raw?.id ?? raw?.project_id ?? '').trim();
  if (id && !/^\d+$/.test(id) && !/\s/.test(id)) {
    return normalizeProjectSlugForApi(id) || id;
  }
  return '';
}

export function projectSlugFetchCandidates(routeSlug, propertyType = 'apartment') {
  const decoded = decodeRouteSlug(routeSlug);
  const parsed = parseProjectRouteSlug(decoded, propertyType);
  const normalized = normalizeProjectSlugForApi(decoded);

  const seoCandidates = [];
  const typeWord = getPropertyTypeUrlWord(propertyType);
  const marker = `-${typeWord}-in-`;
  const markerIdx = normalized.lastIndexOf(marker);
  if (markerIdx !== -1) {
    const before = normalized.slice(0, markerIdx);
    seoCandidates.push(...enumerateApiSlugCandidatesFromSeoBefore(before));
  }

  const apiSlug = parsed.apiSlug || normalized;
  return [
    ...new Set([
      ...seoCandidates,
      apiSlug,
      normalized,
      decoded.trim().toLowerCase(),
    ].filter(Boolean)),
  ];
}

function unwrapProjectDetailPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data != null && payload.success !== false) return payload.data;
  return payload;
}

/** Slug from a project detail API payload (typed or untyped). */
export function extractProjectSlugFromDetail(payload) {
  const data = unwrapProjectDetailPayload(payload);
  if (!data || typeof data !== 'object') return '';

  const projectObj =
    data.project ||
    data.apartment ||
    data.villa ||
    data.plot ||
    data.apartment_buy_detail ||
    data.villa_buy_detail ||
    data.plot_buy_detail ||
    data.project_detail ||
    data;

  const candidates = [
    projectObj?.slug,
    projectObj?.project_slug,
    projectObj?.url_slug,
    data.slug,
    data.project_slug,
    data.url_slug,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeProjectSlugForApi(candidate);
    if (normalized) return normalized;
  }
  return '';
}

/** Parse slug segment from `.../projects/{slug}` API/cache URL. */
export function parseProjectSlugFromDetailUrl(urlStr) {
  try {
    const url = new URL(urlStr);
    const match = url.pathname.match(/\/projects\/([^/]+)$/i);
    if (!match) return '';
    return normalizeProjectSlugForApi(decodeURIComponent(match[1]));
  } catch {
    return '';
  }
}

/** Reject CDN/client cache payloads that belong to a different project. */
export function projectDetailSlugMatchesRequest(payload, routeSlug, propertyType = 'apartment') {
  const returned = extractProjectSlugFromDetail(payload);
  if (!returned) return false;

  const candidates = projectSlugFetchCandidates(routeSlug, propertyType);
  return candidates.some((requested) => requested === returned);
}

function hasProjectDetailShape(data) {
  const unwrapped = unwrapProjectDetailPayload(data);
  return (
    unwrapped &&
    (unwrapped.project ||
      unwrapped.apartment ||
      unwrapped.villa ||
      unwrapped.plot ||
      unwrapped.title ||
      unwrapped.name ||
      unwrapped.id != null ||
      unwrapped.slug ||
      unwrapped.apartment_buy_detail ||
      unwrapped.villa_buy_detail ||
      unwrapped.plot_buy_detail ||
      unwrapped.project_detail)
  );
}

function normalizeFetchErrorMessage(err, fallback = 'Project not found') {
  const msg = String(err?.response?.data?.message || err?.message || fallback);
  if (/signal timed out|aborted|timeout/i.test(msg)) {
    return 'This project is taking longer than usual to load. Please try again in a moment.';
  }
  return msg || fallback;
}

/** Try normalized slug first, then raw route segment (legacy links). */
export async function fetchProjectDetailBySlug(routeSlug, typeParam, fetchDataFn, propertyType = 'apartment') {
  const candidates = projectSlugFetchCandidates(routeSlug, propertyType);
  let lastMessage = 'Project not found';

  for (const trySlug of candidates) {
    try {
      // Always bypass client cache for PDP open — upstream CDN cache was returning wrong projects.
      const res = await fetchDataFn(
        `projects/${trySlug}`,
        { type: typeParam },
        { skipCache: true },
      );
      const data = res?.success !== false && res?.data != null ? res.data : res;
      if (hasProjectDetailShape(data) && projectDetailSlugMatchesRequest(data, trySlug, propertyType)) {
        return { data, apiSlug: trySlug, response: res };
      }
      lastMessage = res?.message || lastMessage;
    } catch (e) {
      lastMessage = normalizeFetchErrorMessage(e, lastMessage);
    }
  }

  return { data: null, apiSlug: candidates[0] || '', error: lastMessage };
}
